import {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
  type ReactElement,
} from 'react';
import { DayDivider } from '../../components/wsj-feed/DayDivider';
import { FeedLightbox } from '../../components/wsj-feed/FeedLightbox';
import { FeedMessage } from '../../components/wsj-feed/FeedMessage';
import { FeedSettings } from '../../components/wsj-feed/FeedSettings';
import { LastReadDivider } from '../../components/wsj-feed/LastReadDivider';
import { NewMessagesPill } from '../../components/wsj-feed/NewMessagesPill';
import { useLastRead } from '../../hooks/useLastRead';
import { useWsjFeed } from '../../hooks/useWsjFeed';
import { useWsjFeedNotifications } from '../../hooks/useWsjFeedNotifications';
import type { FeedMessage as FeedMessageType } from '../../services/wsjFeedService';
import { navigateToTicker } from '../../utils/tickerLinks';

function formatDate(isoString: string): string {
  const date = new Date(isoString);
  const today = new Date();
  if (date.toDateString() === today.toDateString()) return 'Today';
  const yesterday = new Date(today);
  yesterday.setDate(yesterday.getDate() - 1);
  if (date.toDateString() === yesterday.toDateString()) return 'Yesterday';
  return date.toLocaleDateString([], { month: 'short', day: 'numeric' });
}

/**
 * Mobile WSJ feed (no dashboard widget chrome).
 * Same hooks as WsjFeedWidget.
 */
export function MobileWsjFeed(): ReactElement {
  const scrollRef = useRef<HTMLDivElement>(null);
  const [isNearTop, setIsNearTop] = useState(true);
  const [showJumpToLatest, setShowJumpToLatest] = useState(false);

  const {
    settings: notifSettings,
    isAudioPrimed,
    notificationPermission,
    toggleSound,
    toggleDesktop,
    notifyNewMessage,
  } = useWsjFeedNotifications();

  const {
    messages,
    isLoading,
    isConnected,
    fetchNextPage,
    hasNextPage,
    isFetchingNextPage,
    reachedDayEnd,
    pendingNewCount,
    pendingSinceTime,
    clearPendingNew,
  } = useWsjFeed({ isNearTop, onNewMessage: notifyNewMessage });

  const { lastReadId, updateLastRead } = useLastRead();

  useEffect(() => {
    if (isNearTop && messages.length > 0) {
      updateLastRead(messages[0].id);
    }
  }, [isNearTop, messages, updateLastRead]);

  const handleScroll = useCallback(() => {
    const el = scrollRef.current;
    if (!el) return;
    const nearTop = el.scrollTop < 100;
    setIsNearTop(nearTop);
    setShowJumpToLatest(el.scrollTop > el.clientHeight);
    if (nearTop) clearPendingNew();
    const { scrollTop, scrollHeight, clientHeight } = el;
    if (scrollHeight - scrollTop - clientHeight < 200) {
      if (hasNextPage && !isFetchingNextPage && !reachedDayEnd) {
        fetchNextPage();
      }
    }
  }, [
    hasNextPage,
    isFetchingNextPage,
    reachedDayEnd,
    fetchNextPage,
    clearPendingNew,
  ]);

  const scrollToTop = useCallback(() => {
    scrollRef.current?.scrollTo({ top: 0, behavior: 'smooth' });
    clearPendingNew();
  }, [clearPendingNew]);

  const [lightbox, setLightbox] = useState<{
    open: boolean;
    slides: { src: string }[];
    index: number;
  }>({ open: false, slides: [], index: 0 });

  const handleImageClick = useCallback(
    (images: { url: string; filename: string }[], index: number) => {
      setLightbox({
        open: true,
        slides: images.map((img) => ({ src: img.url })),
        index,
      });
    },
    [],
  );

  const liveLabel = useMemo(
    () => (isConnected ? 'Live' : 'Offline'),
    [isConnected],
  );

  return (
    <div className="flex flex-col h-full min-h-0">
      <div className="flex items-center justify-between gap-2 px-3 py-2 border-b border-dark-border bg-dark-surface shrink-0">
        <div className="flex items-center gap-2 min-w-0">
          <span
            className={`w-2 h-2 rounded-full shrink-0 ${isConnected ? 'bg-bull-blue' : 'bg-bear-red'}`}
          />
          <span className="text-xs text-gray-400">{liveLabel}</span>
        </div>
        <FeedSettings
          soundEnabled={notifSettings.soundEnabled}
          desktopEnabled={notifSettings.desktopEnabled}
          isAudioPrimed={isAudioPrimed}
          notificationPermission={notificationPermission}
          onToggleSound={toggleSound}
          onToggleDesktop={toggleDesktop}
        />
      </div>

      <div
        ref={scrollRef}
        onScroll={handleScroll}
        className="flex-1 min-h-0 overflow-y-auto p-3 space-y-3 relative overscroll-contain"
      >
        <NewMessagesPill
          count={pendingNewCount}
          sinceTime={pendingSinceTime}
          onClick={scrollToTop}
        />

        {isLoading ? (
          <div className="flex items-center justify-center py-12 text-gray-500 text-sm">
            Loading…
          </div>
        ) : messages.length === 0 ? (
          <div className="flex items-center justify-center py-12 text-gray-500 text-sm">
            No messages yet
          </div>
        ) : (
          <>
            {messages.map((msg: FeedMessageType, idx: number) => {
              const nextMsg =
                idx < messages.length - 1 ? messages[idx + 1] : null;
              const showDayBoundary =
                nextMsg &&
                formatDate(msg.created_at) !== formatDate(nextMsg.created_at);
              const showLastRead =
                lastReadId !== null && msg.id === lastReadId && idx > 0;

              return (
                <div key={msg.id}>
                  <FeedMessage
                    message={msg}
                    onImageClick={handleImageClick}
                    onTickerClick={navigateToTicker}
                  />
                  {showLastRead && <LastReadDivider />}
                  {showDayBoundary && nextMsg && (
                    <DayDivider label={formatDate(nextMsg.created_at)} />
                  )}
                </div>
              );
            })}
            {isFetchingNextPage && (
              <div className="text-center py-2 text-gray-500 text-xs">
                Loading older messages…
              </div>
            )}
          </>
        )}

        {showJumpToLatest && (
          <button
            type="button"
            onClick={scrollToTop}
            className="sticky bottom-2 left-full -translate-x-full z-30 min-h-[44px] min-w-[44px] bg-accent-yellow text-hot-tag-text rounded-full shadow-lg flex items-center justify-center"
            title="Jump to latest"
          >
            ↑
          </button>
        )}
      </div>

      <FeedLightbox
        open={lightbox.open}
        slides={lightbox.slides}
        index={lightbox.index}
        onClose={() => setLightbox((prev) => ({ ...prev, open: false }))}
      />
    </div>
  );
}
